(ns common.schema.entities.counterparty-diligence.templates.capman.project
  (:require
   [common.schema.entities.counterparty-diligence.templates.capman.legal-entity :as capman-legal-entity-template]
   [common.schema.entities.counterparty-diligence.templates.capman.natural-person :as capman-natural-person-template]
   [common.schema.entities.counterparty-diligence.templates.questions.legal-entity :as legal-entity-q]
   [common.schema.entities.counterparty-diligence.templates.questions.person :as person-q]
   [common.schema.entities.counterparty-diligence.templates.questions.common :as common-q]
   [potpuri.core :as p]
   [clojure.string :as string]
   [common.schema.entities.counterparty-diligence.templates.util :as tu]
   [common.schema.entities.counterparty-diligence.templates.validation :as validation]
   [common.util :as cu]))

(def template-id :counterparty-diligence-template/capman-project)

(defn find-legal-entities-with-key-persons
  [{:keys [data]}]
  (let [find-key-persons-fn (:template/find-key-persons capman-legal-entity-template/schema)
        legal-entities-with-key-persons
        (->> data
             :legal-entities
             (mapv (fn [{:keys [_id
                                legal-entity/company-name
                                legal-entity/business-id
                                legal-entity/jurisdiction
                                role-in-transaction
                                role-in-transaction-other]
                         :as legal-entity}]
                     (let [company-name (-> company-name
                                            str
                                            string/trim)
                           key-persons (find-key-persons-fn {:data legal-entity})]
                       {:_id          _id
                        :company-name company-name
                        :business-id  business-id
                        :country      jurisdiction
                        :role-in-transaction role-in-transaction
                        :role-in-transaction-other role-in-transaction-other
                        :key-persons key-persons})))
             (filterv (fn [{:keys [company-name]}]
                        (seq company-name))))]
    legal-entities-with-key-persons))

(defn find-natural-persons
  [{:keys [data]}]
  ;; NOTE: Capman uses :person/first-names (plural) in their templates,
  ;; but we normalize to :first-name (singular) for screening profile compatibility.
  (->> data
       :natural-persons
       (mapv (fn [{:keys [_id person/first-names person/last-name
                          person/date-of-birth
                          role-in-transaction role-in-transaction-other]
                   :as person}]
               (let [first-name (-> first-names
                                    str
                                    string/trim)
                     last-name (-> last-name
                                   str
                                   string/trim)
                     countries (tu/nationality-countries person)]
                 {:_id           _id
                  :first-name    first-name
                  :last-name     last-name
                  :date-of-birth date-of-birth
                  :country       (first countries)
                  :countries     countries
                  :role-in-transaction role-in-transaction
                  :role-in-transaction-other role-in-transaction-other})))
       (filterv (fn [{:keys [first-name last-name]}]
                  (and (seq first-name)
                       (seq last-name))))))

(def legal-entity-template
  (let [updated-schema (-> capman-legal-entity-template/schema
                           :template/schema
                           (dissoc :signatories)
                           (merge
                            (->> [common-q/role-in-transaction
                                  common-q/role-in-transaction-other]
                                 (p/index-by :field))))

        updated-sections (-> capman-legal-entity-template/schema
                             :template/sections
                             (dissoc :signatories))
        updated-sections (->> updated-sections
                              (mapv (fn [[section-id section-config]]
                                      (let [updated-config
                                            (if-not (= section-id :basic-information)
                                              section-config
                                              (update section-config
                                                      :fields
                                                      (fn [prev-section-fields]
                                                        (vec
                                                         (concat
                                                          [(:field common-q/role-in-transaction)
                                                           (:field common-q/role-in-transaction-other)]
                                                          prev-section-fields)))))]
                                        [section-id updated-config])))
                              (into {}))
        updated-template (-> capman-legal-entity-template/schema
                             (assoc :template/schema updated-schema)
                             (assoc :template/sections updated-sections))]
    updated-template))

(def natural-person-template
  (let [updated-schema (-> capman-natural-person-template/schema
                           :template/schema
                           (dissoc :signatories)
                           (merge
                            (->> [common-q/role-in-transaction
                                  common-q/role-in-transaction-other]
                                 (p/index-by :field))))

        updated-sections (-> capman-natural-person-template/schema
                             :template/sections
                             (dissoc :signatories))
        updated-sections (->> updated-sections
                              (mapv (fn [[section-id section-config]]
                                      (let [updated-config
                                            (if-not (= section-id :basic-information)
                                              section-config
                                              (update section-config
                                                      :fields
                                                      (fn [prev-section-fields]
                                                        (vec
                                                         (concat
                                                          [(:field common-q/role-in-transaction)
                                                           (:field common-q/role-in-transaction-other)]
                                                          prev-section-fields)))))]
                                        [section-id updated-config])))
                              (into {}))
        updated-template (-> capman-natural-person-template/schema
                             (assoc :template/schema updated-schema)
                             (assoc :template/sections updated-sections))]
    updated-template))

(def schema
  {:template/id template-id
   :template/template-name "Project"

   :template/form-title    {:en "Transaction Counterparty KYC"}

   :template/notifications
   [{:on-status       :counterparty-diligence.status/review
     :notification-id :notification/counterparty-diligence-review}]

   :template/form-preface
   {:en (str "<p>In accordance with Anti-Money Laundering (AML) and Counter-Terrorist Financing (CTF) regulations, CapMan Group is required to collect specific Know Your Customer (KYC) information on all transaction counterparties.</p>"
             "<br/>"
             "<p>As part of the legal due diligence process, the nominated counsel is requested to gather and review the required documentation of all counterparties of the transaction, and to promptly report any suspicious findings or material observations to CapMan.</p>"
             "<br/>"
             "<p>The Transaction Counterparty KYC check must be carried out on all transaction counterparties:</p>"
             "<ul>"
             "<li>to whom ownership/assets are transferred <strong>directly from CapMan&rsquo;s fund</strong> and if <strong>CapMan&rsquo;s fund is a major counterparty</strong> in the transaction</li>"
             "<li>from whom ownership/assets are transferred <strong>directly to CapMan&rsquo;s fund</strong> and if <strong>CapMan&rsquo;s fund is a major counterparty</strong> in the transaction.</li>"
             "</ul>"
             "<p>Please complete all required information, attach all reviewed materials, and finally confirm the accuracy of the information to the best of your knowledge by signing this form.</p>")}

   :template/find-legal-entities-with-key-persons find-legal-entities-with-key-persons
   :template/find-natural-persons find-natural-persons

   :template/schema
   (merge
    {:fund-name
     {:data-type :data.type/text-simple
      :label     {:en "Fund name"}}

     :project-name
     {:data-type :data.type/text-simple
      :label     {:en "Project name"}}

     :legal-entities
     {:data-type     :data.type/vector
      :editor-type   :editor.type/entity-list
      :item-data-type :data.type/map
      :item-template legal-entity-template
      :entity-label  {:en "Legal entity"}
      :table-columns [legal-entity-q/company-name
                      legal-entity-q/business-id
                      legal-entity-q/jurisdiction
                      common-q/role-in-transaction]
      :item-display-string-fields [legal-entity-q/company-name]
      :linked-fields [:natural-persons]
      :min-items (fn [{:keys [data]}]
                   (if (seq (:natural-persons data)) 0 1))
      :min-items-error :validation.error/at-least-one-counterparty-required}

     :natural-persons
     {:data-type           :data.type/vector
      :editor-type         :editor.type/entity-list
      :item-data-type      :data.type/map
      :item-template       natural-person-template
      :entity-label        {:en "Person"}
      :entity-label-plural {:en "People"}
      :linked-fields       [:legal-entities]
      :table-columns       [person-q/first-names
                            person-q/last-name
                            person-q/nationality
                            person-q/date-of-birth
                            common-q/role-in-transaction]
      :item-display-string-fields [person-q/first-names
                                   person-q/last-name]}}

    {:remarks-confirmation
     {:data-type :data.type/keyword
      :editor-type :editor.type/radio
      :linked-fields [:additional-remarks-text
                      :additional-remarks-attachments]
      :options   [{:value :confirmation-no-remarks
                   :label {:en "I hereby confirm that I have received and reviewed the added and attached and I have nothing to declare in terms of AML, CTF and Sanctions regulation."}}
                  {:value :confirmation-additional-remarks
                   :label {:en "I hereby confirm that I have reviewed the information and made remarks, which should be taken into consideration in the decision-making. Remarks can be found in the text box / document(s) attached below."}}]}

     :additional-remarks-text
     {:label {:en "Additional remarks"}
      :data-type :data.type/text-multiline
      :linked-fields [:additional-remarks-attachments]
      :visibility-fn (fn [{:keys [data]}]
                       (-> data
                           :remarks-confirmation
                           (= :confirmation-additional-remarks)))
      :validation-fn (fn [{:keys [value data draft?]}]
                       (let [additional-remarks? (-> data
                                                     :remarks-confirmation
                                                     (= :confirmation-additional-remarks))
                             additional-remarks-attachments? (-> data
                                                                 :additional-remarks-attachments
                                                                 seq)
                             mandatoriness (if (or draft?
                                                   (not additional-remarks?)
                                                   additional-remarks-attachments?)
                                             :mandatory/optional
                                             :mandatory/always)]
                         (validation/validate-text-field
                          {:field-config {:mandatoriness mandatoriness}}
                          value)))}

     :additional-remarks-attachments
     {:label {:en "Supporting documents"}
      :data-type :data.type/file
      :multiple? true
      :linked-fields [:additional-remarks-text]
      :validation-fn (fn [{:keys [value data draft? entity-lookup]}]
                       (let [additional-remarks? (-> data
                                                     :remarks-confirmation
                                                     (= :confirmation-additional-remarks))
                             additional-remarks-text? (-> data
                                                          :additional-remarks-text
                                                          cu/blank-value?
                                                          not)
                             mandatoriness (if (or draft?
                                                   (not additional-remarks?)
                                                   additional-remarks-text?)
                                             :mandatory/optional
                                             :mandatory/always)
                             args {:entity-lookup entity-lookup
                                   :field-config {:multiple? true
                                                  :mandatoriness mandatoriness}}]
                         (validation/validate-file-field args value)))
      :visibility-fn (fn [{:keys [data]}]
                       (-> data
                           :remarks-confirmation
                           (= :confirmation-additional-remarks)))}}

    {(:field common-q/signatories) common-q/signatories})


   :template/sections
   {:project-information
    {:id    :project-information
     :ord   0
     :title {:en "Project information"}
     :fields [:fund-name :project-name]}

    :legal-entities
    {:ord   1
     :title {:en "Legal entities"}
     :fields [:legal-entities]}

    :natural-persons
    {:ord   2
     :title {:en "Natural persons"}
     :fields [:natural-persons]}

    :confirmation
    {:ord 3
     :title {:en "Confirmation"}
     :fields [:remarks-confirmation
              :additional-remarks-text
              :additional-remarks-attachments]}

    :signatories
    {:ord                4
     :title              {:en "Signatories"}
     :info-text {:en (str "<p>If you have the authority to sign this form, please fill in your name and email below.</p>"
                          "<br/>"
                          "<p>If you do not have the authority to sign this form, please fill in the name and email address of the person from your company who does have the authority to sign.</p>")}
     :info-text-position :top
     :signatory-section? true
     :fields [:signatories]}}})
